import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Gift, Check, User, Phone } from "lucide-react";

var QUIZ_DATA = [
  {
    question: "Какую челюсть вы хотите восстановить?",
    options: ["Нижнюю", "Верхнюю", "Обе челюсти"],
  },
  {
    question: "Остались ли свои зубы?",
    options: ["Да", "Нет", "Затрудняюсь ответить"],
  },
  {
    question: "Есть ли снимок челюсти?",
    options: ["Да", "Нет", "Не знаю, подойдёт ли"],
  },
  {
    question: "Вам понадобится справка для налогового вычета?",
    options: ["Да", "Нет", "Затрудняюсь ответить"],
  },
];

var GIFTS = [
  "Бесплатная консультация",
  "Снимок КТ",
  "Удаление зубов",
];

var TOTAL_STEPS = QUIZ_DATA.length + 1;

export default function QuizSection() {
  var navigate = useNavigate();
  var [step, setStep] = useState(0);
  var [answers, setAnswers] = useState({});
  var [selectedOption, setSelectedOption] = useState(null);
  var [name, setName] = useState("");
  var [phone, setPhone] = useState("");
  var [consent, setConsent] = useState(false);
  var [errors, setErrors] = useState({});

  var isFormStep = step === QUIZ_DATA.length;
  var progress = ((step + 1) / TOTAL_STEPS) * 100;

  var handleOptionClick = useCallback(
    function (option) {
      setSelectedOption(option);
      setAnswers(function (prev) {
        var next = Object.assign({}, prev);
        next[step] = option;
        return next;
      });
      setTimeout(function () {
        setSelectedOption(null);
        setStep(function (prev) { return prev + 1; });
      }, 400);
    },
    [step]
  );

  var goBack = function () {
    if (step > 0) {
      setStep(function (prev) { return prev - 1; });
      setSelectedOption(null);
      setErrors({});
    }
  };

  var handleSubmit = function (e) {
    e.preventDefault();
    var newErrors = {};
    if (!name.trim()) newErrors.name = "Введите ваше имя";
    if (!phone.trim() || phone.replace(/\D/g, "").length < 11)
      newErrors.phone = "Введите корректный номер телефона";
    if (!consent) newErrors.consent = "Необходимо согласие";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Яндекс.Метрика — замените 99999999 на реальный ID
    if (window.ym) window.ym(108320505, "reachGoal", "form_submit");

    // Calltouch — отправка заявки из квиза
    // Замените 99999999 (siteId и requestId) на реальные значения
    try {
      var sessionId = window.ct
        ? window.ct("calltracking_params", "bph448nt").sessionId
        : "";
      var payload = {
        fio: name,
        phoneNumber: phone,
        subject: "Заявка из квиза",
        comment: Object.entries(answers)
          .map(function (entry) {
            var q = QUIZ_DATA[entry[0]];
            return q ? q.question + ": " + entry[1] : "";
          })
          .join("; "),
        requestUrl: window.location.href,
        sessionId: sessionId,
      };
      fetch(
        "https://api.calltouch.ru/calls-service/RestAPI/requests/63062/register/",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(payload).toString(),
        }
      ).catch(function () {});
    } catch (err) {
      // Calltouch не загружен
    }

    navigate("/thank-you");
  };

  var formatPhone = function (value) {
    var digits = value.replace(/\D/g, "");
    if (digits.length === 0) return "";
    if (digits[0] === "8") digits = "7" + digits.slice(1);
    if (digits[0] !== "7") digits = "7" + digits;
    var formatted = "+7";
    if (digits.length > 1) formatted += " (" + digits.slice(1, 4);
    if (digits.length >= 4) formatted += ")";
    if (digits.length > 4) formatted += " " + digits.slice(4, 7);
    if (digits.length > 7) formatted += "-" + digits.slice(7, 9);
    if (digits.length > 9) formatted += "-" + digits.slice(9, 11);
    return formatted;
  };

  var handlePhoneChange = function (e) {
    var formatted = formatPhone(e.target.value);
    setPhone(formatted);
    if (errors.phone) setErrors(function (prev) { return Object.assign({}, prev, { phone: "" }); });
  };

  return (
    <section id="quiz-section" data-testid="quiz-section" className="py-16 md:py-24 bg-gradient-to-b from-brand-50 to-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2 data-testid="quiz-title" className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Рассчитайте стоимость за 1 минуту
          </h2>
          <p className="text-base md:text-lg text-slate-500 mb-6">
            Ответьте на 4 вопроса и получите 3 подарка:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {GIFTS.map(function (gift) {
              return (
                <div key={gift} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-brand-700 bg-gradient-to-r from-brand-50 to-brand-100">
                  <Gift className="w-4 h-4 flex-shrink-0" />
                  {gift}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quiz card */}
        <div data-testid="quiz-card" className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Progress */}
          <div className="px-6 md:px-10 pt-6 md:pt-8">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-500">
                {isFormStep ? "Последний шаг" : "Вопрос " + (step + 1) + " из " + QUIZ_DATA.length}
              </span>
              <span className="text-sm font-medium text-brand-600">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="h-2 bg-brand-100 rounded-full overflow-hidden">
              <div
                data-testid="quiz-progress"
                className="h-full bg-brand-600 rounded-full transition-all duration-500"
                style={{ width: progress + "%" }}
              />
            </div>
          </div>

          <div className="px-6 md:px-10 py-8 md:py-10">
            {!isFormStep ? (
              <div key={step} className="animate-fade-in">
                <h3 data-testid="quiz-question" className="font-heading text-xl md:text-2xl font-bold mb-6">
                  {QUIZ_DATA[step].question}
                </h3>
                <div className="flex flex-col gap-3">
                  {QUIZ_DATA[step].options.map(function (option) {
                    var isSelected = selectedOption === option;
                    var wasPrev = answers[step] === option;
                    return (
                      <button
                        key={option}
                        data-testid={"quiz-option-" + option}
                        onClick={function () { handleOptionClick(option); }}
                        className={
                          "flex items-center gap-4 p-4 md:p-5 rounded-2xl border-2 text-left text-base md:text-lg font-medium transition-all duration-200 cursor-pointer min-h-[56px] " +
                          (isSelected
                            ? "border-brand-600 bg-brand-50 text-brand-700"
                            : wasPrev
                            ? "border-brand-200 bg-brand-50 text-brand-700"
                            : "border-slate-200 bg-white hover:border-brand-400 hover:bg-brand-50")
                        }
                      >
                        <div
                          className={
                            "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors " +
                            (isSelected || wasPrev ? "border-brand-600 bg-brand-600" : "border-slate-300")
                          }
                        >
                          {(isSelected || wasPrev) && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="animate-fade-in">
                <h3 data-testid="form-title" className="font-heading text-xl md:text-2xl font-bold mb-2">
                  Отлично! Куда отправить расчёт?
                </h3>
                <p className="text-base text-slate-500 mb-8">
                  Оставьте контакты — мы перезвоним в течение 15 минут
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        data-testid="form-name"
                        type="text"
                        placeholder="Ваше имя"
                        value={name}
                        onChange={function (e) {
                          setName(e.target.value);
                          if (errors.name) setErrors(function (p) { return Object.assign({}, p, { name: "" }); });
                        }}
                        className={"w-full h-14 pl-12 pr-4 text-lg bg-slate-50 border rounded-xl outline-none focus:ring-2 focus:ring-brand-600/20 focus:border-brand-600 transition-colors " + (errors.name ? "border-red-400" : "border-slate-200")}
                      />
                    </div>
                    {errors.name && <p data-testid="error-name" className="text-red-500 text-sm mt-1.5 ml-1">{errors.name}</p>}
                  </div>

                  <div>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        data-testid="form-phone"
                        type="tel"
                        placeholder="+7 (___) ___-__-__"
                        value={phone}
                        onChange={handlePhoneChange}
                        className={"w-full h-14 pl-12 pr-4 text-lg bg-slate-50 border rounded-xl outline-none focus:ring-2 focus:ring-brand-600/20 focus:border-brand-600 transition-colors " + (errors.phone ? "border-red-400" : "border-slate-200")}
                      />
                    </div>
                    {errors.phone && <p data-testid="error-phone" className="text-red-500 text-sm mt-1.5 ml-1">{errors.phone}</p>}
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      data-testid="form-consent"
                      type="checkbox"
                      checked={consent}
                      onChange={function (e) {
                        setConsent(e.target.checked);
                        if (errors.consent) setErrors(function (p) { return Object.assign({}, p, { consent: "" }); });
                      }}
                      className="mt-1 w-5 h-5 rounded border-slate-300 accent-brand-600"
                    />
                    <span className="text-sm text-slate-500 leading-relaxed">
                      Я даю согласие на обработку своих персональных данных согласно{" "}
                      <a href="https://nasha-stoma.ru/politika/" target="_blank" rel="noopener noreferrer" className="text-brand-600 underline">
                        Политике конфиденциальности
                      </a>
                    </span>
                  </label>
                  {errors.consent && <p data-testid="error-consent" className="text-red-500 text-sm -mt-2 ml-1">{errors.consent}</p>}

                  <button
                    data-testid="form-submit"
                    type="submit"
                    className="h-14 text-lg font-medium rounded-full bg-brand-600 hover:bg-brand-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl mt-2 cursor-pointer inline-flex items-center justify-center gap-2"
                  >
                    Получить расчёт стоимости
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              </div>
            )}
          </div>

          {step > 0 && (
            <div className="px-6 md:px-10 pb-6 md:pb-8">
              <button
                data-testid="quiz-back"
                onClick={goBack}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                Назад
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
